import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { DeferredSceneRenderer } from 'noodle/engine/renderer/DeferredSceneRenderer';
import {
  GraphicsSettings,
  GraphicsPreset,
  GRAPHICS_PRESETS,
  graphicsPreset,
  detectPreset,
  defaultGraphicsSettings,
} from 'noodle/engine/renderer/GraphicsSettings';

const STORAGE_KEY = 'noodle-graphics-settings';

@Component({
  selector: 'app-graphics-settings',
  templateUrl: './graphics-settings.html',
  styleUrl: './graphics-settings.css',
})
export class GraphicsSettingsPanel implements OnInit {
  @Input({ required: true }) renderer!: DeferredSceneRenderer;
  @Output() closed = new EventEmitter<void>();

  readonly presets = GRAPHICS_PRESETS;
  activePreset: GraphicsPreset | null = 'medium';

  settings: GraphicsSettings = defaultGraphicsSettings();

  ngOnInit(): void {
    this.settings = { ...this.renderer.graphicsSettings };
    this.activePreset = detectPreset(this.settings);
  }

  selectPreset(preset: GraphicsPreset): void {
    this.settings = graphicsPreset(preset);
    this.activePreset = preset;
    this.renderer.applySettings(this.settings);
    this.persist();
  }

  updateSetting<K extends keyof GraphicsSettings>(key: K, value: GraphicsSettings[K]): void {
    this.settings[key] = value;
    this.renderer.applySettings({ [key]: value });
    this.activePreset = detectPreset(this.settings);
    this.persist();
  }

  close(): void {
    this.closed.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  private persist(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
  }

  static loadSaved(): Partial<GraphicsSettings> | null {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      const validKeys = new Set(Object.keys(defaultGraphicsSettings()));
      const filtered: Partial<GraphicsSettings> = {};
      for (const key of Object.keys(parsed)) {
        if (validKeys.has(key)) {
          (filtered as any)[key] = parsed[key];
        }
      }
      return Object.keys(filtered).length > 0 ? filtered : null;
    } catch {
      return null;
    }
  }
}
